/**
 * Settings → New Construction (NC-DRAW-02). The company-wide default draw
 * split for builder houses. Each builder's own split (and who pulls their
 * permits, PO required, billing email) lives on Jobs → New Construction →
 * Builder Settings; any single house can still be changed on its Money tab.
 * Stored in org_setting nc_draw_split_default.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useSaveOrgSetting } from "@/lib/orgSettings";
import { useNcDrawDefault } from "@/features/jobs/ncBilling";
import { DrawSplitEditor } from "@/features/jobs/NCBillingPanel";

export function NewConstructionSettings() {
  const canEdit = useCan("settings.money");
  const { data, isLoading } = useNcDrawDefault();
  const save = useSaveOrgSetting();
  const qc = useQueryClient();
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          Default Draw Split
          <InfoTip label="About the default draw split">
            Used when neither the builder nor the house has its own split; each draw is a real invoice to the builder with
            the house&apos;s PO on it. A builder&apos;s own split is under Jobs → New Construction → the builder&apos;s row →
            Builder Settings.
          </InfoTip>
        </h3>
        {isLoading || !data ? (
          <div className="mt-4 h-24 animate-pulse rounded-lg bg-slate-100" />
        ) : canEdit ? (
          <div className="mt-4">
            <DrawSplitEditor
              key={JSON.stringify(data)}
              value={data}
              saving={save.isPending}
              saveLabel="Save Default Split"
              onSave={(v) => save.mutate({ key: "nc_draw_split_default", value: v }, { onSuccess: () => qc.setQueryData(["org_setting", "nc_draw_split_default"], v) })}
            />
            {save.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t save — {save.error instanceof Error ? save.error.message : "try again."}</p>}
          </div>
        ) : (
          <p className="mt-3 text-sm font-semibold text-slate-800">{data.map((s) => `${s.label} ${s.pct}%`).join(" · ")}</p>
        )}
      </Card>
    </div>
  );
}
